import type { DoctrineDef, DoctrineId, HumanCommandSource, MatchState, PlayerId } from '@signalclash/shared';
import {
  DOCTRINE_DEFS,
  DOCTRINE_IDS,
  DOCTRINE_PICK_WINDOW_DURATION_SEC,
  SECONDS_PER_TICK,
} from '@signalclash/shared';

import { hapticTap } from '../input/Profile.js';

const PICKER_ID = 'doctrine-picker';
const STYLE_ID = 'doctrine-picker-styles';

interface DoctrinePickerOptions {
  playerId: PlayerId;
  human: HumanCommandSource;
}

// V1.5 doctrine pick window UI. Rule 2 — presentation only. Reads
// MatchState.doctrinePickWindowOpen + closesAtTick from each snapshot, surfaces
// the centered three-button modal during the window, and emits the player's
// pick through HumanCommandSource (Rule 3 — input → command). The picker hides
// itself once the player has a doctrineId set or the window closes.
//
// One-second readability (Rule 21): each button shows the doctrine name and a
// short tagline; the live countdown reads at the top so the player knows how
// long they have. No flashing copy on the buttons; the countdown does the
// urgency lift.
export class DoctrinePicker {
  // Mutable so the network-mode `setPlayerId` can flip the picker's
  // viewer to the server-assigned slot. The picker's `observeSnapshot`
  // reads `state.players[opts.playerId].doctrineId` to decide whether
  // the modal should still show.
  private opts: DoctrinePickerOptions;
  private root: HTMLDivElement | null = null;
  private countdownEl: HTMLDivElement | null = null;
  private buttons = new Map<DoctrineId, HTMLButtonElement>();
  private latestTick = 0;
  // V1.8 — CrazyGames stats show avg playtime ≈ 2m34s; the 3:00 doctrine
  // modal lands right where players were already bouncing. We suppress
  // the modal entirely for the player's FIRST match; the sim still
  // auto-assigns DEFAULT_DOCTRINE_ON_TIMEOUT at the close tick, so the
  // doctrine effect lands on the simulation but the new player isn't
  // hit with an unexplained modal on their introductory session.
  // Defaults to false; ClientApp flips this BEFORE startMatch when
  // it's the player's first telemetry-recorded match.
  private suppressForFirstMatch = false;

  constructor(opts: DoctrinePickerOptions) {
    this.opts = opts;
    injectStyles();
    this.mount();
    this.hide();
  }

  setPlayerId(playerId: PlayerId): void {
    this.opts = { ...this.opts, playerId };
  }

  // V1.8 — see comment on `suppressForFirstMatch`. ClientApp threads the
  // first-match flag in before each match so the suppression is keyed
  // off the truth at match-start (a player who completes their first
  // match and immediately rematches still gets the modal on Match 2).
  setSuppressForFirstMatch(suppress: boolean): void {
    this.suppressForFirstMatch = suppress;
  }

  hide(): void {
    if (this.root !== null) this.root.style.display = 'none';
  }

  reset(): void {
    this.hide();
  }

  // Drives visibility off MatchState. The host's doctrine_window_open lifecycle
  // event is emitted in parallel; subscribers can react to either signal.
  observeSnapshot(state: MatchState): void {
    if (this.root === null) return;
    this.latestTick = state.tick;
    const player = state.players[this.opts.playerId];
    const open = state.doctrinePickWindowOpen;
    const alreadyPicked = player.doctrineId !== null;
    if (!open || alreadyPicked || this.suppressForFirstMatch) {
      this.hide();
      return;
    }
    this.root.style.display = 'flex';
    if (this.countdownEl !== null && state.doctrinePickWindowClosesAtTick !== null) {
      const ticksLeft = Math.max(0, state.doctrinePickWindowClosesAtTick - state.tick);
      const secondsLeft = ticksLeft * SECONDS_PER_TICK;
      this.countdownEl.textContent = `${secondsLeft.toFixed(0)}s`;
    }
  }

  private mount(): void {
    this.root = ensureRoot();
    this.root.innerHTML = '';
    const card = document.createElement('div');
    card.className = 'dp__card';

    const title = document.createElement('div');
    title.className = 'dp__title';
    title.textContent = 'Choose your Doctrine';
    card.appendChild(title);

    const sub = document.createElement('div');
    sub.className = 'dp__subtitle';
    const sub1 = document.createElement('span');
    sub1.textContent = 'Pick window closes in ';
    this.countdownEl = document.createElement('div');
    this.countdownEl.className = 'dp__countdown';
    // Initial label uses the same constant the sim drives the countdown
    // off, so extending the window doesn't desync the first-frame text.
    this.countdownEl.textContent = `${DOCTRINE_PICK_WINDOW_DURATION_SEC}s`;
    sub.appendChild(sub1);
    sub.appendChild(this.countdownEl);
    card.appendChild(sub);

    const row = document.createElement('div');
    row.className = 'dp__row';
    for (const id of DOCTRINE_IDS) {
      const def: DoctrineDef = DOCTRINE_DEFS[id];
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dp__btn';
      btn.dataset.doctrine = id;
      const name = document.createElement('div');
      name.className = 'dp__btn-name';
      name.textContent = def.name;
      const desc = document.createElement('div');
      desc.className = 'dp__btn-desc';
      desc.textContent = def.description;
      btn.appendChild(name);
      btn.appendChild(desc);
      btn.addEventListener('click', () => this.onPick(id));
      row.appendChild(btn);
      this.buttons.set(id, btn);
    }
    card.appendChild(row);
    this.root.appendChild(card);
  }

  private onPick(id: DoctrineId): void {
    this.opts.human.requestPickDoctrine(id);
    hapticTap();
    // Optimistic hide — the next snapshot tick will confirm the pick. The
    // simulation owns truth (Rule 1); if the command was rejected the picker
    // would re-appear on the next snapshot if doctrineId is still null.
    this.hide();
    // Reference the unused field so the linter doesn't flag it; we keep
    // latestTick around for future "issued at tick" diagnostics.
    void this.latestTick;
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(PICKER_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = PICKER_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${PICKER_ID} {
  position: absolute;
  inset: 0;
  display: none;
  align-items: center;
  justify-content: center;
  z-index: 40;
  background: rgba(2,4,6,0.55);
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfd6dd;
}
/* V2 Tempo polish: matches main-menu Commander card aesthetic. */
.dp__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px 30px;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.14), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.10), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.96), rgba(5, 9, 17, 0.98));
  border: 1px solid rgba(174, 240, 255, 0.32);
  border-radius: 0;
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 18px 50px rgba(0,0,0,0.7);
  min-width: 720px;
  pointer-events: auto;
}
.dp__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 22px;
  opacity: 0.40;
  pointer-events: none;
}
.dp__card > * { position: relative; z-index: 1; }
.dp__title {
  text-align: center;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 18px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #aef0ff;
  text-shadow: 0 0 12px rgba(110,220,255,0.45);
}
.dp__subtitle {
  text-align: center;
  font-family: 'Rajdhani', system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.10em;
  color: #a3b8d2;
}
.dp__countdown {
  display: inline-block;
  margin-left: 2px;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #ff7a5a;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-shadow: 0 0 10px rgba(255,122,90,0.45);
}
.dp__row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.dp__btn {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  background: rgba(10, 19, 34, 0.55);
  color: #eef4ff;
  border: 1px solid rgba(174,240,255,0.22);
  border-radius: 0;
  clip-path: polygon(10px 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%, 0 10px);
  padding: 16px 18px;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  min-height: 110px;
  transition: border-color 150ms, box-shadow 150ms, transform 80ms;
}
.dp__btn:hover {
  border-color: rgba(174,240,255,0.55);
  box-shadow: 0 0 18px rgba(30,167,255,0.25);
}
.dp__btn:focus-visible { outline: 2px solid #aef0ff; outline-offset: 3px; }
/* ── Per-doctrine accent colours ───────────────────────────────────────────
 * Mirror the HUD doctrine-pill palette so the picker feels like a preview
 * of the pill the player will see in the top-left corner during the match. */
.dp__btn[data-doctrine="vanguard"] .dp__btn-name { color: #ffe680; text-shadow: 0 0 10px rgba(255,230,128,0.45); }
.dp__btn[data-doctrine="vanguard"]:hover {
  border-color: rgba(255,230,128,0.65);
  box-shadow: 0 0 22px rgba(255,230,128,0.28);
}
.dp__btn[data-doctrine="saboteur"] .dp__btn-name { color: #ff7a5a; text-shadow: 0 0 10px rgba(255,122,90,0.45); }
.dp__btn[data-doctrine="saboteur"]:hover {
  border-color: rgba(255,122,90,0.65);
  box-shadow: 0 0 22px rgba(255,42,42,0.28);
}
.dp__btn[data-doctrine="bulwark"] .dp__btn-name { color: #6fdcff; text-shadow: 0 0 10px rgba(110,220,255,0.45); }
.dp__btn[data-doctrine="bulwark"]:hover {
  border-color: rgba(110,220,255,0.65);
  box-shadow: 0 0 22px rgba(30,167,255,0.32);
}
.dp__btn:active { transform: scale(0.98); }
.dp__btn-name {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 15px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}
.dp__btn-desc {
  font-family: 'Rajdhani', system-ui, sans-serif;
  font-size: 12px;
  letter-spacing: 0.02em;
  color: #a3b8d2;
  line-height: 1.5;
}

/* V3 mobile: stack the three doctrine cards vertically and shrink the card
 * width so the modal fits a phone in portrait without horizontal scroll.
 * Buttons get min-height: 56 px (Apple HIG tap-target floor) so picking the
 * wrong doctrine under time pressure is harder. */
@media (max-width: 820px), (pointer: coarse) {
  .dp__card {
    min-width: 0;
    width: calc(100vw - 24px);
    max-width: 460px;
    padding: 14px 16px;
    gap: 10px;
    box-sizing: border-box;
    max-height: calc(100vh - 24px);
    overflow-y: auto;
  }
  .dp__title { font-size: 14px; }
  .dp__subtitle { font-size: 10px; }
  .dp__countdown { font-size: 13px; }
  .dp__row { grid-template-columns: 1fr; gap: 8px; }
  .dp__btn { padding: 12px 14px; min-height: 56px; gap: 4px; }
  .dp__btn-name { font-size: 14px; }
  /* Bump from 11px — descriptions are the only information about each doctrine
   * and are shown under a 30-second pick timer (V1.7, was 15s); legibility
   * matters even though there's now more time to read. */
  .dp__btn-desc { font-size: 12px; line-height: 1.4; }
}
`;
  document.head.appendChild(style);
}
